import * as Sentry from '@sentry/nextjs'
import { useCallback } from 'react'
import { SubmitErrorHandler, useFormContext } from 'react-hook-form'
import { SIGNUP_STEPS } from '../config/signup'
import { useSignupFlow } from '../flows/useSignupFlow'
import { useCaptchaStoreActions } from '../store/useCaptchaStore'
import {
  useSignupStoreActions,
  useSignupStoreStep,
} from '../store/useSignupStore'
import { AuthenticatedUser } from '../types/auth0'
import { EnumSignupErrors, EnumSignupSteps } from '../types/signup'
import { SignupFormData } from './useSignupForm'

interface Props {
  authenticatedUser?: AuthenticatedUser | null
}

export const useSignupFormSubmitHandlers = ({ authenticatedUser }: Props) => {
  const currentStep = useSignupStoreStep()
  const { resetWidgetInstance } = useCaptchaStoreActions()

  const {
    goToStep,
    clearSignupError,
    setSignupError,
    updateSignupData,
    goToNextStep,
    resetSignupData,
  } = useSignupStoreActions()

  const { getValues, clearErrors } = useFormContext<SignupFormData>()

  const { signup } = useSignupFlow()

  const onFormValid = useCallback(
    async (formData: SignupFormData) => {
      clearSignupError()

      // Update Context
      updateSignupData(formData)

      try {
        if (!authenticatedUser) {
          // Create user in Auth0
          await signup(formData)
        }

        resetSignupData()

        goToStep(EnumSignupSteps.EMAIL_CONFIRM)
      } catch (error) {
        resetWidgetInstance()
        setSignupError(EnumSignupErrors.TECHNICAL_ERROR)
        goToStep(EnumSignupSteps.PASSWORD)
        Sentry.captureException(error, { extra: { signupData: formData } })
      }
    },
    [
      clearSignupError,
      goToStep,
      resetSignupData,
      setSignupError,
      signup,
      updateSignupData,
      authenticatedUser,
      resetWidgetInstance,
    ]
  )

  const onFormInvalid: SubmitErrorHandler<SignupFormData> = useCallback(
    (errors) => {
      const formFieldsCurrentStep =
        SIGNUP_STEPS.find((item) => item.stepName === currentStep)
          ?.formFields || []
      if (formFieldsCurrentStep.some((item) => errors[item])) {
        return
      }

      // As submitting triggers a validation,
      // we need to clear errors or next steps will be marked as invalid
      clearErrors()

      updateSignupData(getValues())
      goToNextStep()
    },
    [clearErrors, currentStep, getValues, goToNextStep, updateSignupData]
  )

  return { onFormValid, onFormInvalid }
}
